import { useId, useState } from 'react';
import { useI18n } from '../i18n';
import {
  validateWorkerCreditsPayment,
  workerCreditsPaymentIsValid,
  type WorkerCreditsPaymentFieldErrors,
  type WorkerCreditsPaymentInput,
} from '../utils/businessWorkerCreditPayment';
import styles from './BookingPaymentPanel.module.css';

export type BookingWorkerCreditsPaymentPanelProps = {
  amountLabel: string | null;
  initialNationalId?: string | null;
  initialBusinessId?: string | null;
  onPayWithCredits: (input: WorkerCreditsPaymentInput) =>
    | boolean
    | Promise<boolean>
    | { success: boolean; errorMessage?: string }
    | Promise<{ success: boolean; errorMessage?: string }>;
  onOpenRegularPayment: () => void;
  onCancel: () => void;
  processing?: boolean;
};

function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

export function BookingWorkerCreditsPaymentPanel({
  amountLabel,
  initialNationalId = null,
  initialBusinessId = null,
  onPayWithCredits,
  onOpenRegularPayment,
  onCancel,
  processing = false,
}: BookingWorkerCreditsPaymentPanelProps) {
  const { t, language } = useI18n();
  const fieldId = useId();
  const dir = language === 'he' ? 'rtl' : 'ltr';

  const [nationalId, setNationalId] = useState(() => initialNationalId?.trim() ?? '');
  const [businessId, setBusinessId] = useState(() => initialBusinessId?.trim() ?? '');
  const [fieldErrors, setFieldErrors] = useState<WorkerCreditsPaymentFieldErrors>({});
  const [error, setError] = useState<string | null>(null);

  const messages = {
    errorNationalId: t('bookings.workerCredits.errorNationalId'),
    errorBusinessId: t('bookings.workerCredits.errorBusinessId'),
  };

  const clearFieldError = (key: keyof WorkerCreditsPaymentFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (processing) {
      return;
    }
    const errors = validateWorkerCreditsPayment({ nationalId, businessId }, messages);
    setFieldErrors(errors);
    if (!workerCreditsPaymentIsValid(errors)) {
      setError(errors.nationalId ?? errors.businessId ?? null);
      return;
    }
    setError(null);
    const payResult = await onPayWithCredits({ nationalId, businessId });
    const payFailed =
      typeof payResult === 'boolean' ? !payResult : payResult.success === false;
    if (payFailed) {
      const detail =
        typeof payResult === 'object' && payResult !== null && 'errorMessage' in payResult
          ? payResult.errorMessage
          : undefined;
      setError(detail ?? t('bookings.payment.errorBookingFailed'));
    }
  };

  const payLabel = amountLabel
    ? t('bookings.workerCredits.pay', { amount: amountLabel })
    : t('bookings.workerCredits.payNoAmount');

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('bookings.workerCredits.title')}
      dir={dir}
    >
      <div className={styles.sheet}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>{t('bookings.workerCredits.title')}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            disabled={processing}
            aria-label={t('bookings.payment.cancel')}
          >
            ✕
          </button>
        </div>
        <p className={styles.subtitle}>{t('bookings.workerCredits.subtitle')}</p>

        <div className={styles.amountBox}>
          <div className={styles.amountSummary}>
            <span className={styles.amountLabel}>{t('bookings.payment.amountDue')}</span>
            <span className={styles.amountVatNote}>{t('bookings.payment.vatIncluded')}</span>
          </div>
          <span className={styles.amountValue}>{amountLabel ?? '—'}</span>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <label className={styles.field} htmlFor={`${fieldId}-national-id`}>
            <span className={styles.fieldLabel}>{t('bookings.workerCredits.nationalId')}</span>
            <input
              id={`${fieldId}-national-id`}
              className={`${styles.input} ${styles.inputLtr}${fieldErrors.nationalId ? ` ${styles.inputInvalid}` : ''}`}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              dir="ltr"
              required
              placeholder={t('bookings.payment.nationalIdPlaceholder')}
              value={nationalId}
              onChange={(e) => {
                setNationalId(digitsOnly(e.target.value).slice(0, 9));
                clearFieldError('nationalId');
              }}
              disabled={processing}
              aria-invalid={fieldErrors.nationalId ? true : undefined}
            />
            {fieldErrors.nationalId ? (
              <span className={styles.fieldError} role="alert">
                {fieldErrors.nationalId}
              </span>
            ) : null}
          </label>

          <label className={styles.field} htmlFor={`${fieldId}-business-id`}>
            <span className={styles.fieldLabel}>{t('bookings.workerCredits.businessId')}</span>
            <input
              id={`${fieldId}-business-id`}
              className={`${styles.input} ${styles.inputLtr}${fieldErrors.businessId ? ` ${styles.inputInvalid}` : ''}`}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              dir="ltr"
              required
              placeholder={t('bookings.workerCredits.businessIdPlaceholder')}
              value={businessId}
              onChange={(e) => {
                setBusinessId(digitsOnly(e.target.value).slice(0, 12));
                clearFieldError('businessId');
              }}
              disabled={processing}
              aria-invalid={fieldErrors.businessId ? true : undefined}
            />
            {fieldErrors.businessId ? (
              <span className={styles.fieldError} role="alert">
                {fieldErrors.businessId}
              </span>
            ) : null}
          </label>

          <p className={styles.secureNote}>{t('bookings.workerCredits.otpSoon')}</p>

          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className={styles.payButton} disabled={processing}>
            {processing ? t('bookings.payment.processing') : payLabel}
          </button>

          <button
            type="button"
            className={styles.cancelButton}
            onClick={onOpenRegularPayment}
            disabled={processing}
          >
            {t('bookings.workerCredits.openRegularPayment')}
          </button>

          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={processing}
          >
            {t('bookings.payment.cancel')}
          </button>
        </form>
      </div>
    </div>
  );
}
